'use client';
import { useRef, useCallback, useMemo, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useIsomorphicLayoutEffect } from '@financial/common-hooks';
import { isBrowser } from '../utils/env.mjs';

function usePortal() {
  const portal = useRef();
  const createRoot = useCallback(() => {
    const randomId = Math.random().toString(36).substring(2);
    const id = `portal__${randomId}`;
    if (!isBrowser) {
      return;
    }
    if (!portal.current) {
      const root = document.createElement('div');
      root.id = id;
      portal.current = root;
      document.body.appendChild(root);
    } else if (document.getElementById(id)) {
      portal.current = document.getElementById(id);
    }
  }, []);
  createRoot();
  useIsomorphicLayoutEffect(() => {
    return () => {
      var _portal$current;
      if ((_portal$current = portal.current) !== null && _portal$current !== void 0 && _portal$current.parentNode) {
        const {
          parentNode
        } = portal.current;
        parentNode.removeChild(portal.current);
        portal.current = null;
      }
    };
  }, []);
  const Portal = useMemo(() => /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
      children
    } = _ref;
    if (!portal.current) {
      return null;
    }
    if (ref) {
      ref.current = portal.current;
    }
    return /*#__PURE__*/createPortal(children, portal.current);
  }), []);
  return Portal;
}

export { usePortal as default };
